import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';

import { CompanyParametersQuery, CurrencyCollectionQuery, CurrencyItem, SaleGridItem } from '@skylab-shared/business';
import { BusinessObjectBase, ModelBase, RepositoryBase, RepositoryResponseCode, RepositorySaveResponse } from '@skylab-shared/core';
import { DocumentPaymentListItemModel, PurchaseDocumentKind } from '@skylab-shared/dataModel';

import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { EInvoiceReceivedGridItem, PurchaseEInvoiceDocumentBO, PurchaseEInvoiceDocumentRepository, PurchaseExpenseDocumentBO, PurchaseExpenseDocumentRepository, PurchaseGridItem, PurchaseInvoiceAdjustmentDocumentBO, PurchaseInvoiceAdjustmentDocumentRepository, PurchaseInvoiceDocumentBO, PurchaseInvoiceDocumentRepository } from '@skylab-angular-shared/business';
import { L10n, L10nBase, RootContext, RootContextBase } from '@skylab-angular-shared/core';
import { CompanyContextLink, TranslatePipe } from '@skylab-angular-shared/pipes';
import { BooleanValueAccessorDirective, ButtonComponent, DatepickerValueAccessorDirective, DialogContentComponentBase, DialogContentComponentIns, DialogInsService, FormContainerComponent, InsDatepickerDirective, NumericValueAccessorDirective, ToastService, TooltipDirective, ValidateDirective, ValidateInnerDirective, ValidationSourceDirective, IconComponent } from '@skylab-angular-shared/ui';
import { NavigationRoute } from '@skylab-shared/scenes';
import { Calc, MidpointRounding } from '@skylab-shared/utils';
import { DocumentsRouting } from '../../documents/documents-routing';
import { ViewValidation, ViewValidationService } from '../../form/form-validation';

export interface PurchasePaymentData {
    entity: PurchaseGridItem | EInvoiceReceivedGridItem;
    onSave: (newEntity: { version: string; leftToPay: number; }) => Promise<void>;
}

type BO = PurchaseExpenseDocumentBO | PurchaseInvoiceDocumentBO | PurchaseInvoiceAdjustmentDocumentBO | PurchaseEInvoiceDocumentBO;

interface PaymentDialogViewModel {
    bo: BO;
    isSaving: boolean;
    getDocumentRoute(): NavigationRoute;
    save: () => Promise<void>;
    currency: CurrencyItem;
    payment: { value: number; date: Date; onDocument: boolean; sun: string };
    isEInvoiceDocument: boolean;
}

@Component({
    selector: 'skl-purchase-payment-dialog',
    templateUrl: './purchase-service-payment-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [RouterLink, CompanyContextLink, DialogContentComponentIns, TooltipDirective, FormContainerComponent, ValidationSourceDirective, InsDatepickerDirective, DatepickerValueAccessorDirective, FormsModule, ValidateDirective, NumericValueAccessorDirective, ValidateInnerDirective, BooleanValueAccessorDirective, ButtonComponent, TranslatePipe, IconComponent]
})
export class PurchaseServicePaymentDialogComponent extends DialogContentComponentBase<PaymentDialogViewModel, PurchasePaymentData> {
    public isFormReady = false;
    public viewValidation!: ViewValidation;

    public constructor(
        @Inject(L10n) private readonly l10n: L10nBase,
        private readonly purchaseInvoiceRepo: PurchaseInvoiceDocumentRepository,
        private readonly purchaseInvoiceAdjustmentRepo: PurchaseInvoiceAdjustmentDocumentRepository,
        private readonly purchaseEInvoiceRepo: PurchaseEInvoiceDocumentRepository,
        private readonly purchaseExpenseRepo: PurchaseExpenseDocumentRepository,
        @Inject(RootContext) private readonly rootContext: RootContextBase,
        private readonly viewValidationService: ViewValidationService,
        protected override readonly dialogService: DialogInsService,
        private readonly toastService: ToastService
    ) {
        super(dialogService);
    }

    public override async ngOnInit(): Promise<void> {
        await this.prepareDataAsync();
        this.isFormReady = true;
    }

    protected override async prepareDataAsync(): Promise<void> {
        try {
            const entity = this.data.entity;
            const repo = this.getRepository();
            const bo = await repo.loadAsync(this.id, this.rootContext.createLocalContext('documentPaymentHintbox'));
            await bo.waitForProcessingCompletionAsync();
            bo.attachReadonlyChecker();

            const payment = new DocumentPaymentListItemModel();
            const companyParams = bo.localContext.getPreloaded(CompanyParametersQuery);
            const systemCurrency = companyParams?.systemCurrency;
            if (!systemCurrency) return;
            const currency = bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });
            if (!currency) return;

            this.viewValidation = this.viewValidationService.create([bo]);

            bo.model.payments.push(payment);
            payment.value = Math.abs(entity.leftToPay);
            payment.date = new Date();
            payment.onDocument = false;

            const self = this;
            this.viewModel = {
                bo,
                isEInvoiceDocument: bo.model.kind === PurchaseDocumentKind.EPurchaseInvoice,
                currency: currency,
                payment: {
                    get value() {
                        return payment.value;
                    },
                    set value(value) {
                        payment.value = Math.abs(value);

                    },
                    get date() {
                        return payment.date;
                    },
                    set date(date) {
                        payment.date = date;
                    },
                    get onDocument() {
                        return payment.onDocument;
                    },
                    set onDocument(onDocument: boolean) {
                        payment.onDocument = onDocument;
                    },
                    get sun() {
                        return payment.sun;
                    },

                },
                isSaving: false,
                save: async () => {
                    await bo.waitForProcessingCompletionAsync();
                    if (!bo.isValid) return;

                    this.viewModel.isSaving = true;
                    try {
                        const result = await this.saveAsync(bo);
                        if (result.code === RepositoryResponseCode.Success && result.object) {
                            const newLeftToPay = this.getLeftToPayValue(this.viewModel.payment.value);
                            this.data.onSave({
                                version: result.object.version,
                                leftToPay: Calc.round(newLeftToPay, currency.precision, MidpointRounding.AwayFromZero)
                            });
                        } else if (result.code === RepositoryResponseCode.ExternalError) {
                            this.toastService.showAsync({
                                type: 'error',
                                text: this.l10n.instant('scenes.shared.form.form-state-context.external-error.message'),
                            });
                        }
                        this.onCancel();
                    } catch (err) {
                        this.logger.error(err);
                    } finally {
                        this.viewModel.isSaving = false;
                    }
                },
                getDocumentRoute() {
                    return {
                        url: DocumentsRouting.getEditState(self.data.entity.kind).url,
                        params: {id: self.id}
                    }
                },
            };
        } catch (err) {
            this.logger.error(err);
            this.onCancel();
        }
    }

    public override ngOnDestroy(): void {
        this.viewValidation?.dispose();
        this.viewModel?.bo.dispose();
    }


    private getLeftToPayValue(valueOfLastPayment: number): number {
        return this.data.entity.leftToPay > 0 ? this.data.entity.leftToPay - valueOfLastPayment : this.data.entity.leftToPay + valueOfLastPayment;
    }


    private saveAsync(bo: BusinessObjectBase<ModelBase>): Promise<RepositorySaveResponse> {
        const repo = this.getRepository() as RepositoryBase<BusinessObjectBase<ModelBase>, ModelBase>;
        return repo.saveAsync(bo);
    }

    private getRepository(): PurchaseInvoiceDocumentRepository | PurchaseInvoiceAdjustmentDocumentRepository | PurchaseEInvoiceDocumentRepository | PurchaseExpenseDocumentRepository {
        switch (this.data.entity.kind) {
            case PurchaseDocumentKind.PurchaseInvoice:
                return this.purchaseInvoiceRepo;
            case PurchaseDocumentKind.PurchaseInvoiceAdjustment:
                return this.purchaseInvoiceAdjustmentRepo;
            case PurchaseDocumentKind.EPurchaseInvoice:
                return this.purchaseEInvoiceRepo;
            case PurchaseDocumentKind.Expense:
                return this.purchaseExpenseRepo;
        }
    }

    private get id(): number {
        if (isEInvoicesGridItem(this.data.entity)) {
            return this.data.entity.documentId;
        } else {
            return this.data.entity.id;
        }
    }
}


export function isEInvoicesGridItem(model: PurchaseGridItem | EInvoiceReceivedGridItem | SaleGridItem): model is EInvoiceReceivedGridItem {
    return model.hasOwnProperty(<keyof EInvoiceReceivedGridItem>'invoiceReferenceNumber');
}
